import type { PublicProductSort } from '@repo/api-contracts';
import { SlidersHorizontal } from 'lucide-react';
import { type ReactElement, useId } from 'react';
import type { StoreProductsParams } from '@/entities/product';
import { STORE_FILTER_DRAWER_ID } from '@/shared/config';
import { formatCount } from '@/shared/lib';
import { cn } from '@/shared/ui';
import { toActiveFilterChips } from './active-filter-chips';

const SORT_LABELS: Readonly<Record<PublicProductSort, string>> = {
  RECOMMENDED: '추천순',
  PRICE_ASC: '낮은 가격순',
  PRICE_DESC: '높은 가격순',
};

const SORT_ORDER: readonly PublicProductSort[] = ['RECOMMENDED', 'PRICE_ASC', 'PRICE_DESC'];

interface StoreToolbarProps {
  /** 첫 페이지에서만 내려온다 — null 이면 표시하지 않는다. */
  readonly totalCount: number | null;
  /** 지금 조건 — 정렬 표시와 "필터" 버튼의 걸린 조건 수에 쓴다. */
  readonly filters: StoreProductsParams;
  readonly onSortChange: (sort: PublicProductSort) => void;
  /** 필터 드로어를 연다 — 모든 폭에서 이 버튼이 유일한 필터 진입점이다. */
  readonly onOpenFilters: () => void;
  readonly isFilterDrawerOpen: boolean;
}

/**
 * 목록 툴바 — 테두리 없이 왼쪽 "총 N개 상품", 오른쪽 얇은 세로선으로 나눈 글자 정렬과 테두리 알약 "필터".
 * "필터" 버튼에는 드로어 조건(색상 수 + 수량 + 단가) 개수를 작은 검정 원으로 단다.
 * 카테고리·기획전은 탭·띠가 따로 보여 주므로 세지 않는다.
 */
export const StoreToolbar = ({
  totalCount,
  filters,
  onSortChange,
  onOpenFilters,
  isFilterDrawerOpen,
}: StoreToolbarProps): ReactElement => {
  const activeFilterCount = toActiveFilterChips(filters).length;
  const filterCountId = useId();

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4">
      {/* 전체 상품 수를 모르는 동안은 글자 없는 회색 칸으로 자리를 지킨다. */}
      {totalCount === null ? (
        <span aria-hidden className="block h-5 w-28 bg-muted" />
      ) : (
        <p className="text-[15px] text-muted-foreground">
          총{' '}
          <span className="font-bold text-foreground tabular-nums">{formatCount(totalCount)}</span>
          개 상품
        </p>
      )}

      <div className="flex items-center gap-5 sm:gap-7">
        <fieldset
          aria-label="정렬"
          className="flex items-center divide-x divide-foreground/15 text-[13px] sm:text-sm"
        >
          {SORT_ORDER.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={filters.sort === value}
              onClick={() => onSortChange(value)}
              className={cn(
                'cursor-pointer px-2.5 leading-none whitespace-nowrap transition-colors first:pl-0 last:pr-0 sm:px-3.5',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                filters.sort === value
                  ? 'font-bold text-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {SORT_LABELS[value]}
            </button>
          ))}
        </fieldset>

        <button
          type="button"
          aria-expanded={isFilterDrawerOpen}
          aria-controls={isFilterDrawerOpen ? STORE_FILTER_DRAWER_ID : undefined}
          // 개수는 이름이 아니라 설명으로 읽힌다 — 버튼 이름은 언제나 "필터" 하나로 둔다.
          aria-describedby={activeFilterCount > 0 ? filterCountId : undefined}
          onClick={onOpenFilters}
          className="group inline-flex h-10 cursor-pointer items-center gap-2 rounded-full border border-foreground px-4 text-sm font-medium transition-colors hover:bg-foreground hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <SlidersHorizontal aria-hidden strokeWidth={1.75} className="size-4" />
          필터
          {activeFilterCount > 0 ? (
            <span
              aria-hidden
              // 버튼이 호버로 검정 채움이 되면 원은 반대로 흰 바탕이 된다.
              className="inline-flex size-5 items-center justify-center rounded-full bg-foreground text-[11px] font-semibold text-background tabular-nums transition-colors group-hover:bg-background group-hover:text-foreground"
            >
              {activeFilterCount}
            </span>
          ) : null}
        </button>
        {activeFilterCount > 0 ? (
          <span id={filterCountId} className="sr-only">
            적용된 조건 {activeFilterCount}개
          </span>
        ) : null}
      </div>
    </div>
  );
};
